<template>
  <div class="loan-option-card" :position="position">
    <div class="loan-option-card-header">
      <div class="loan-option-card-title">
        {{ cardTitle }}
        <tooltip-component v-if="hasRepaymentTermTooltip">
          <template #toggle>
            <sup><icon icon="info" /></sup>
          </template>
          <div class="repayment-terms-tooltip">
            {{ item?.repaymentTermDetails?.tooltipText }}
          </div>
        </tooltip-component>
      </div>
      <div class="divider"></div>
      <div class="rate">
        <div class="rate-value">
          {{ item.rateDisplay
          }}<variable-rate-tooltip v-if="isVariableRate" location="left" :option="item" />
        </div>
        <div>Interest Rate</div>
      </div>
    </div>
    <div class="loan-option-card-content">
      <repayment-options-header
        class="loan-option-card-repayments-header mobile"
        :inSchoolLabel="details?.monthlyInSchoolPaymentLabel"
        :postSchoolLabel="details?.monthlyPostSchoolPaymentLabel"
      />
      <ul class="loan-option-card-repayments">
        <li
          v-for="repayment in item.repayments"
          :key="repayment.repaymentTypeName"
          :class="{
            active: repayment === selectedRepaymentOption,
          }"
          @click="updateRepaymentOption(repayment)"
        >
          <pel-loan-repayment-option
            :key="repayment.repaymentTypeName"
            :ratePlan="item"
            :repayment="repayment"
            :isMobile="isMobile"
            :position="position"
            :active="repayment === selectedRepaymentOption"
          />
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, PropType } from "vue";
import { useStore } from "vuex";
import PelLoanRepaymentOption from "@/components/pel-loan-configuration/PelLoanRepaymentOption.vue";
import RepaymentOptionsHeader from "@/components/pel-loan-configuration/RepaymentOptionsHeader.vue";
import VariableRateTooltip from "@/components/pel-loan-configuration/VariableRateTooltip.vue";
import { RateTypeIds } from "@/config/constants";
import { RatePlanVM } from "@/models/loanConfiguration";
import { PelLoanConfigurationDetails } from "@/models/pel";
import TooltipComponent from "@/components/shared/TooltipComponent.vue";
import Icon from "@/components/shared/Icon.vue";

const store = useStore();

const props = defineProps({
  item: {
    type: Object as PropType<RatePlanVM>,
    required: true,
  },
  position: {
    type: String,
    default: "",
  },
});

const windowWidth = ref<number>(0);

const details = computed<PelLoanConfigurationDetails>(
  () => store.state.loanConfigurationModule.applicationInfo,
);

const savingChanges = computed<boolean>(() => store.state.loanConfigurationModule.savingChanges);

const hasRepaymentTermTooltip = computed<boolean>(() => {
  return (
    (props.item &&
      props.item?.repaymentTermDetails &&
      props.item?.repaymentTermDetails?.tooltipText !== null &&
      props.item?.repaymentTermDetails?.tooltipText !== undefined) === true
  );
});

const selectedRepaymentOption = computed(
  () => store.state.loanConfigurationModule.selectedRepaymentOption,
);

const isMobile = computed(() => windowWidth.value <= 576 && windowWidth.value >= 390);
const isXSmallMobile = computed(() => windowWidth.value <= 390);
const isVariableRate = computed<boolean>(() => props.item.rateTypeId === RateTypeIds.VARIABLE);

const cardTitle = computed(() =>
  isMobile.value || isXSmallMobile.value
    ? `${props.item.repaymentTerm} Year Term`
    : `${props.item.repaymentTerm} Year Repayment Term`,
);

async function updateRepaymentOption(repayment: any) {
  if (windowWidth.value <= 576 && !savingChanges.value) {
    await store.dispatch("loanConfigurationModule/setSelectedRatePlan", props.item);
    await store.dispatch("loanConfigurationModule/setSelectedRepaymentOption", repayment);
  }
}

function handleResize() {
  windowWidth.value = window.innerWidth;
}

onMounted(async () => {
  window.addEventListener("resize", handleResize);
  handleResize();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
});
</script>

<style scoped>
.loan-option-card {
  width: 100%;
  font-family: var(--header-font);
}

.loan-option-card-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  position: relative;
  margin-bottom: 1rem;
}
.loan-option-card-title {
  font-size: 1.375rem;
  font-weight: var(--font-weight-bold);
}
.loan-option-card-header .rate {
  display: flex;
  flex-direction: column;
  font-size: 0.875rem;
  font-weight: var(--font-weight-normal);
}

.loan-option-card-header .rate .rate-value {
  font-size: 1.5rem;
  font-weight: var(--font-weight-bold);
}

.loan-option-card-repayments {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

ul.loan-option-card-repayments {
  display: block;
  text-decoration: none;
  margin: 0;
  padding: 0;
}

ul.loan-option-card-repayments li {
  list-style: none;
  background: #ffffff;
  border-radius: 6px;
  margin-bottom: 1rem;
}

.loan-option-card-repayments-header.mobile {
  display: none;
}

.repayment-terms-tooltip {
  color: #000;
  font-family: var(--header-font);
  font-size: 0.75rem;
  font-style: normal;
  font-weight: var(--font-weight-normal);
  line-height: 15px;
}

@media (max-width: 576.98px) {
  ul.loan-option-card-repayments li.active {
    border: 2px solid var(--theme-color);
  }

  .divider {
    border-left: 1px solid #ced1d9;
    display: block;
    height: auto;
  }
  .loan-option-card-title {
    font-size: 16px;
    line-height: 15px; /* 93.75% */
    font-weight: var(--font-weight-bold);
    display: flex;
    align-items: center;
  }
  .loan-option-card-header .rate {
    display: flex;
    position: relative;
    font-size: 16px;
    font-weight: var(--font-weight-normal);
  }

  .loan-option-card-header .rate .rate-value {
    font-size: 16px;
    font-weight: var(--font-weight-bold);
    margin-right: 6px;
  }

  .loan-option-card-repayments-header.mobile {
    display: contents;
  }
}

@media (max-width: 390px) {
  .divider {
    display: none;
  }

  .loan-option-card-repayments-header.mobile {
    display: none;
  }

  .loan-option-card-header {
    flex-direction: row;
    margin-bottom: 0;
  }

  .loan-option-card-title {
    align-items: center;
  }

  .loan-option-card-header .rate .rate-value {
    margin-right: 0;
  }
  ul.loan-option-card-repayments li {
    padding-right: 0;
  }
}
</style>
